<template>
  <div v-if="localBatchIds.length > 0" class="resume-progress-container mb-4">
    <div class="w-full">
      
      <!-- Top Row: Current Action & Count -->
      <div class="flex justify-between items-center mb-2">
        <div class="flex items-center">
            <!-- Spinner if running -->
            <div v-if="!isComplete" class="spinner mr-2"></div>
            
            <div class="flex flex-col">
                <span class="text-sm font-bold text-dark truncate max-w-xs">
                    {{ currentActionText }}
                </span>
                <span class="text-xs text-gray-500">
                   {{ aggregatedStatus.processed_count }} / {{ aggregatedStatus.total_files }} files processed
                </span>
            </div>
        </div>
        
        <!-- Overall Percentage -->
        <span class="font-bold text-lg text-primary">{{ Math.floor(displayPercentage) }}%</span>
      </div>

      <!-- Main Progress Bar (Overall Average) -->
      <div class="progress-bar-track">
          <div
              class="progress-bar-fill"
              :class="isComplete ? 'bg-success' : 'bg-warning'"
              :style="{ width: displayPercentage + '%' }"
          >
          </div>
      </div>
    
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

const API_BASE_URL = process.env.VUE_APP_API_URL;

export default {
    name: "ResumeUploadProgress",
    data() {
        return {
            localBatchIds: [], 
            batchesData: {},
            pollingInterval: null,
            stateKey: null
        };
    },
    computed: {
        ...mapState('organization', ['activeResumeBatchId', 'activeBatchProgress']),

        aggregatedStatus() {
            let totalFiles = 0;
            let processedCount = 0;
            let allFiles = {};
            let statuses = [];

            this.localBatchIds.forEach(bid => {
                const data = this.batchesData[bid];
                if (data) {
                    totalFiles += (data.total_files || 0);
                    processedCount += (data.processed_count || 0);
                    statuses.push(data.status);
                    if (data.files) Object.assign(allFiles, data.files);
                }
            });

            let globalStatus = 'pending';
            if (this.localBatchIds.length > 0) {
                const allDone = statuses.every(s => ['completed', 'failed'].includes(s));
                globalStatus = (allDone && statuses.length > 0) ? 'completed' : 'processing';
            }

            return { status: globalStatus, total_files: totalFiles, processed_count: processedCount, files: allFiles };
        },

        isComplete() {
            return this.localBatchIds.length > 0 && this.aggregatedStatus.status === 'completed';
        },

        displayPercentage() {
            if (this.isComplete) return 100;
            // Use Vuex state directly for width to avoid jumping
            return this.activeBatchProgress || 0;
        },

        currentActionText() {
            if (this.isComplete) return 'Processing Complete';
            
            // Find the first file that is not done
            const files = this.aggregatedStatus.files;
            const activeFile = Object.keys(files).find(name => {
                const s = files[name].status;
                return s !== 'completed' && s !== 'failed' && s !== 'processed_and_emailed';
            });
            
            if (activeFile) return `Parsing ${activeFile}...`;
            return 'Processing Resumes...';
        }
    },
    watch: {
        // Critical: Watch Vuex. If Modal updates the batch ID, we reload LS to see new files.
        '$store.state.organization.activeResumeBatchId': {
            handler(newVal) {
                if (!newVal) {
                    // Cleared by Modal
                    this.clearState();
                } else {
                    // New batch added, or page refresh.
                    this.$nextTick(() => {
                        this.loadState();
                        if(!this.isComplete) this.startPolling();
                    });
                }
            }
        }
    },
    
    mounted() {
        const orgId = this.$store.state.organization.orgData._id || 'default';
        this.stateKey = `resume_upload_state_${orgId}`;
        this.loadState();
        if (this.localBatchIds.length > 0 && !this.isComplete) {
            this.startPolling();
        }
    },
    
    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        loadState() {
            try {
                const saved = localStorage.getItem(this.stateKey);
                if (!saved) return;
                const state = JSON.parse(saved);
                this.localBatchIds = state.localBatchIds || [];
                this.batchesData = state.batchesData || {};
            } catch (e) {}
        },

        clearState() {
            this.localBatchIds = [];
            this.batchesData = {};
            this.stopPolling();
        },

        startPolling() {
            if (this.pollingInterval) return;
            this.fetchStatuses();
            this.pollingInterval = setInterval(this.fetchStatuses, 2000);
        },
        
        stopPolling() {
            if (this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; }
        },

        async fetchStatuses() {
            if (this.localBatchIds.length === 0) return;
            try {
                // Just fetch data to show current file name. 
                // We rely on Vuex for the percentage width to stay in sync with modal.
                const ids = this.localBatchIds.join(',');
                const res = await fetch(`${API_BASE_URL}/status/multi-batch?batch_ids=${ids}&organization_id=${this.$store.state.organization.orgData._id}`);
                if (res.ok) {
                    const data = await res.json();
                    if(data.batches) {
                        Object.keys(data.batches).forEach(bid => {
                            this.$set(this.batchesData, bid, data.batches[bid]);
                        });
                    }
                    if (this.isComplete) {
                        this.stopPolling();
                        setTimeout(() => this.clearState(), 5000); // Optional auto-hide
                    }
                }
            } catch (e) {}
        }
    }
}
</script>

<style scoped>
.resume-progress-container {
    background: #fff;
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
    border: 1px solid #f1f1f1;
    border-radius: 12px;
    padding: 1rem;
}

.progress-bar-track {
  width: 100%; height: 6px; background-color: #f0f2f5;
  border-radius: 3px; overflow: hidden;
}

.progress-bar-fill {
  height: 100%; transition: width 0.3s ease;
}

.bg-success { background-color: #28C76F; }
.bg-warning { background-color: #FFC107; }
.text-primary { color: #7367F0; }

.spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #FFC107;
  animation: spin 1s ease-in-out infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>